import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	calculateApiKeyPrice,
	getApiKeyPriceMultiplier,
	PRICE_PER_WORD,
	roundPrice,
	WORDS_MAX,
	WORDS_MIN,
	WORDS_STEP,
} from "@textformatter/shared";

function formatPrice(value: number) {
	return new Intl.NumberFormat("sk-SK", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);
}

type ApiKeyPricingCardProps = {
	title?: string;
	initialWords?: number;
	initialLabel?: string;
	labelPlaceholder?: string;
	buttonText?: string;
	showLabelInput?: boolean;
	onCreate: (params: { words: number; label: string }) => void;
};

export default function ApiKeyPricingCard({
	title = "Create API Key",
	initialWords = WORDS_MIN,
	initialLabel = "",
	labelPlaceholder = "Key 1",
	buttonText = "Create key",
	showLabelInput = true,
	onCreate,
}: ApiKeyPricingCardProps) {
	const [words, setWords] = useState(initialWords);
	const [label, setLabel] = useState(initialLabel);

	const price = useMemo(() => calculateApiKeyPrice(words), [words]);
	const pricePer100k = useMemo(() => {
		const multiplier = getApiKeyPriceMultiplier(words);
		return roundPrice(100000 * PRICE_PER_WORD * multiplier, 2);
	}, [words]);

	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
					<div>
						<p className="text-sm font-semibold text-slate-700">Words: {words.toLocaleString("sk-SK")}</p>
						<input
							className="mt-3 w-full accent-orange-400"
							type="range"
							min={WORDS_MIN}
							max={WORDS_MAX}
							step={WORDS_STEP}
							value={words}
							onChange={(e) => setWords(Number(e.target.value))}
						/>
						<div className="mt-2 flex justify-between text-xs text-slate-500">
							<span>{Math.round(WORDS_MIN / 1000)}k</span>
							<span>
								{WORDS_MAX >= 1000000
									? `${WORDS_MAX / 1000000}M`
									: WORDS_MAX.toLocaleString("sk-SK")}
							</span>
						</div>
						{showLabelInput ? (
							<>
								<label className="mt-5 block text-sm font-semibold text-slate-700">
									API key name (optional)
								</label>
								<input
									className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
									type="text"
									placeholder={labelPlaceholder}
									value={label}
									maxLength={100}
									onChange={(e) => setLabel(e.target.value)}
								/>
								<p className="mt-1 text-right text-xs text-slate-500">{label.length}/100</p>
							</>
						) : null}
					</div>
					<div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
						<p className="text-xs uppercase tracking-[0.2em] text-slate-500">Estimated price</p>
						<p className="mt-2 text-3xl font-bold text-slate-900">
							{formatPrice(price)}{" "}
							<span className="text-2xl text-slate-500">
								per {words.toLocaleString("sk-SK")} words
							</span>
						</p>
						<p className="mt-1 text-xs text-slate-500">
							Volume discount is gradual. Price updates continuously with selected words.
						</p>
						<p className="mt-1 text-xs text-slate-500">
							<strong>{formatPrice(pricePer100k)}</strong> per 100,000 words
						</p>
						<Button
							className="mt-4 w-full"
							onClick={() =>
								onCreate({
									words,
									label: label.trim() || labelPlaceholder,
								})
							}
						>
							{buttonText}
						</Button>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
